// pages/About.tsx
import { motion } from 'framer-motion';
import { Github, Linkedin } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { teamMembers } from '../data/team';
import teamPhoto from '../assets/Team-Photo.jpg';

const About = () => {
  const { darkMode } = useDarkMode();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h1 className="text-5xl font-bold mb-8 text-[#2D6A4F] dark:text-[#B7E4C7]">
              We Are Green Guard: The Champions of Innovation
            </h1>
            <div className="max-w-4xl mx-auto text-[#2D6A4F] dark:text-[#DCE9C2] text-lg leading-relaxed space-y-6 font-medium">
              <p>
                At the Horizon AI Hackathon, we didn't just participate—we dominated. Our team poured in countless hours of research, development, and strategic problem-solving to create Green Guard, an AI-powered financial assistant that is set to revolutionize how people make financial decisions.
              </p>
              <p>
                We took on Track 4: Personal & Behavioral Finance Tools, answering the call for AI-driven financial guidance with an approach that merges behavioral psychology, real-time AI analysis, and investment intelligence.
              </p>
              <p>
                Through meticulous teamwork, innovative thinking, and an unwavering commitment to excellence, we created the best financial assistant AI at the hackathon—one that doesn't just assist, but actively transforms the way users interact with money.
              </p>
            </div>
          </motion.div>

          {/* Team Photo */}
          <div className="mb-20">
            <img 
              src={teamPhoto} 
              alt="Green Guard Team" 
              className="w-full rounded-xl shadow-lg"
            />
          </div>

          {/* Centered Meet the Founders Title */}
          <motion.div 
            className="flex justify-center items-center py-12 mb-20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              className="relative inline-block text-center w-full max-w-4xl mx-auto px-4"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <motion.h2 
                className="text-7xl font-black bg-gradient-to-r from-[#68A511] via-[#85c926] to-[#68A511] bg-clip-text text-transparent relative z-10 tracking-tight"
                initial={{ letterSpacing: "0.1em" }}
                animate={{ letterSpacing: "0.05em" }}
                transition={{ duration: 1 }}
              >
                Meet the Founders
              </motion.h2>
              <motion.div 
                className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 h-2 bg-gradient-to-r from-[#68A511] via-[#85c926] to-[#68A511] rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "80%" }}
                transition={{ duration: 1, delay: 0.3 }}
              />
              <motion.div 
                className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 h-2 bg-gradient-to-r from-[#68A511] via-[#85c926] to-[#68A511] rounded-full blur-xl opacity-50"
                initial={{ width: "0%" }}
                animate={{ width: "80%" }}
                transition={{ duration: 1, delay: 0.3 }}
              />
            </motion.div>
          </motion.div>

          {/* Team Members */}
          <div className="container mx-auto px-4 py-32 pb-40">
            <div className="space-y-32">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className={`flex flex-col md:flex-row items-center gap-12 ${index % 2 === 0 ? '' : 'md:flex-row-reverse'}`}
                >
                  {/* Member Image */}
                  <div className="w-full md:w-1/3">
                    <div className="aspect-[3/4] relative overflow-hidden rounded-xl shadow-lg">
                      <img 
                        src={member.image}
                        alt={member.name}
                        className={`w-full h-full object-cover ${
                          member.name === 'Carys Bernard' 
                            ? 'object-[center_15%]' 
                            : 'object-center'
                        }`}
                      />
                    </div>
                    <div className="flex gap-4 mt-4">
                      {member.social?.github && (
                        <a 
                          href={member.social.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-[#68A511] transition-colors"
                        >
                          <Github size={24} />
                        </a>
                      )}
                      {member.social?.linkedin && (
                        <a 
                          href={member.social.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-[#68A511] transition-colors"
                        >
                          <Linkedin size={24} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Member Info */}
                  <div className="w-full md:w-2/3 space-y-6">
                    <div className="space-y-2">
                      <h3 className="text-2xl font-bold text-[#2D6A4F] dark:text-[#DCE9C2]">
                        {member.name}
                      </h3>
                      <p className="text-xl text-[#2D6A4F] dark:text-[#B7E4C7] font-medium">
                        {member.role}
                      </p>
                      <p className="text-lg text-[#2D6A4F] dark:text-[#B7E4C7] font-medium">
                        {member.title}
                      </p>
                      <p className="text-lg text-[#2D6A4F] dark:text-[#B7E4C7] font-medium">
                        {member.education}
                      </p>
                    </div>
                    
                    <div className="space-y-4 text-[#2D6A4F] dark:text-[#DCE9C2] leading-relaxed">
                      {member.description && member.description.split('\n\n').map((paragraph, i) => (
                        <p key={i} className="text-base">{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;